import { useEffect, useMemo } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Input } from '@/shared/ui/inputs/Input';
import { Select } from '@/shared/ui/selects/Select';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage, extractFieldErrors } from '@/shared/api/errors';
import { apiStaffPost, apiStaffPut } from '@/entities/staff';
import { useRolesList } from '@/entities/roles';
import {
  emptyStaffFormValues,
  staffCreateSchema,
  staffEditSchema,
  type StaffFormValues,
} from '@/features/staff';
import type {
  ApiStaffGetItem,
  ApiStaffPostBody,
  ApiStaffPutBody,
} from '@/shared/types/api/staff';

type Mode = 'create' | 'edit';

type Props = {
  mode: Mode;
  /** Existing staff record for `edit` mode. Required when mode === 'edit'. */
  staff?: ApiStaffGetItem;
  /** When in edit mode and the data is still being fetched. */
  loading?: boolean;
  /** When in edit mode and the record could not be found. */
  notFound?: boolean;
  /** When in edit mode and the load failed for another reason. */
  loadError?: string;
  onRetry?: () => void;
};

const isSuperStaff = (s: ApiStaffGetItem | undefined): boolean => {
  if (!s) return false;
  if (typeof s.is_super === 'boolean') return s.is_super;
  return s.role === null;
};

export const WidgetsStaffFormMain = ({
  mode,
  staff,
  loading,
  notFound,
  loadError,
  onRetry,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const rolesQuery = useRolesList();
  const roles = rolesQuery.data ?? [];

  const schema = mode === 'create' ? staffCreateSchema : staffEditSchema;
  const form = useForm<StaffFormValues>({
    resolver: zodResolver(schema) as Resolver<StaffFormValues>,
    defaultValues: emptyStaffFormValues,
    mode: 'onBlur',
  });
  const { reset } = form;

  const superMode = mode === 'edit' && isSuperStaff(staff);

  // Hydrate edit form once staff loads.
  useEffect(() => {
    if (mode === 'edit' && staff) {
      reset({
        full_name: staff.full_name,
        username: staff.username,
        password: '',
        phone_number: staff.phone_number ?? '',
        role_id: staff.role?.id ?? '',
      });
    }
  }, [mode, staff, reset]);

  const roleOptions = useMemo(
    () =>
      roles
        .filter((r) => !r.is_super)
        .map((r) => ({ value: r.id, label: r.name })),
    [roles],
  );

  const create = useMutate({
    api: (body: ApiStaffPostBody) => apiStaffPost(body),
    invalidateKeys: [queryKeys.staff.all],
    onSuccess: () => {
      toast.success(t('staff.toast.created'));
      navigate('/staff');
    },
    onError: (err, message) => {
      applyServerFieldErrors(err);
      toast.error(message);
    },
  });

  const update = useMutate({
    api: (body: ApiStaffPutBody) => apiStaffPut(staff?.id ?? '', body),
    invalidateKeys: [queryKeys.staff.all],
    onSuccess: () => {
      toast.success(t('staff.toast.updated'));
      navigate('/staff');
    },
    onError: (err, message) => {
      applyServerFieldErrors(err);
      toast.error(message);
    },
  });

  const applyServerFieldErrors = (err: unknown): void => {
    const fieldErrors = extractFieldErrors(err);
    Object.entries(fieldErrors).forEach(([field, msg]) => {
      if (field in emptyStaffFormValues) {
        form.setError(field as keyof StaffFormValues, { type: 'server', message: msg });
      }
    });
  };

  const onSubmit = (v: StaffFormValues): void => {
    if (mode === 'create') {
      create.mutate({
        full_name: v.full_name,
        username: v.username,
        password: v.password,
        phone_number: v.phone_number || undefined,
        role_id: v.role_id,
      });
      return;
    }
    if (!staff) return;
    const body: ApiStaffPutBody = {
      full_name: v.full_name,
      phone_number: v.phone_number || undefined,
    };
    if (v.password) body.password = v.password;
    if (!superMode) body.role_id = v.role_id;
    update.mutate(body);
  };

  const title =
    mode === 'create' ? t('staff.formPage.createTitle') : t('staff.formPage.editTitle');

  // Loading skeleton in edit mode while data is being fetched.
  if (mode === 'edit' && loading) {
    return (
      <>
        <PageHeader title={title} showBack backTo="/staff" />
        <div className="flex min-h-[50vh] items-center justify-center">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  // Not found / load error in edit mode.
  if (mode === 'edit' && (notFound || (!staff && !loading))) {
    return (
      <>
        <PageHeader title={title} showBack backTo="/staff" />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            message={loadError ?? t('staff.formPage.notFound')}
            onRetry={onRetry}
          />
        </div>
      </>
    );
  }

  const submitting = create.isPending || update.isPending;
  const fullNameError = form.formState.errors.full_name?.message;
  const usernameError = form.formState.errors.username?.message;
  const passwordError = form.formState.errors.password?.message;
  const phoneError = form.formState.errors.phone_number?.message;
  const roleError = form.formState.errors.role_id?.message;

  return (
    <>
      <PageHeader title={title} showBack backTo="/staff" />
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 px-4 py-4 lg:px-6 lg:py-6"
        data-cy={mode === 'create' ? 'staff-create-form' : 'staff-edit-form'}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label={t('staff.form.fields.fullName')}
            required
            autoFocus
            error={fullNameError ? t(fullNameError) : undefined}
            {...form.register('full_name')}
          />
          <Input
            label={t('staff.form.fields.username')}
            required
            disabled={mode === 'edit'}
            error={usernameError ? t(usernameError) : undefined}
            {...form.register('username')}
          />
          <Input
            label={
              mode === 'create'
                ? t('staff.form.fields.password')
                : t('staff.form.fields.passwordOptional')
            }
            required={mode === 'create'}
            type="password"
            placeholder={
              mode === 'edit' ? t('staff.form.fields.passwordEditPlaceholder') : undefined
            }
            error={passwordError ? t(passwordError) : undefined}
            {...form.register('password')}
          />
          <Input
            label={t('staff.form.fields.phone')}
            inputMode="tel"
            placeholder={t('staff.form.fields.phonePlaceholder')}
            error={phoneError ? t(phoneError) : undefined}
            {...form.register('phone_number')}
          />
          <div className="sm:col-span-2">
            <Controller
              control={form.control}
              name="role_id"
              render={({ field }) => (
                <Select
                  label={t('staff.form.fields.role')}
                  required
                  name={field.name}
                  disabled={superMode || rolesQuery.isLoading}
                  options={roleOptions}
                  placeholder={t('staff.form.fields.rolePlaceholder')}
                  value={superMode ? '' : field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  error={
                    superMode
                      ? undefined
                      : rolesQuery.error
                      ? t('staff.form.errors.rolesLoadFailed')
                      : roleError
                      ? t(roleError)
                      : undefined
                  }
                />
              )}
            />
            {superMode && (
              <p className="mt-1.5 text-xs text-ink-muted">{t('staff.formPage.superNote')}</p>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
          <div className="mx-auto flex max-w-3xl gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/staff')}
              disabled={submitting}
              block
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting} block>
              {t('common.save')}
            </Button>
          </div>
          {(create.error || update.error) && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {extractErrorMessage(create.error ?? update.error)}
            </p>
          )}
        </div>
      </form>
    </>
  );
};
